import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Video, AudioFeatures, PaginatedResponse } from '@meradio/shared-types';
import * as videosApi from '@/services/videos.api';
import * as channelsApi from '@/services/channels.api';
import { api } from '@/services/api';

export interface VideosState {
  videos: Video[];
  channelVideos: { [channelId: string]: Video[] };
  analyzing: { [videoId: string]: number };
  selectedVideo: Video | null;

  audioFeatures: { [videoId: string]: AudioFeatures };

  likedVideos: string[];

  isLoading: boolean;
  isLoadingChannelVideos: boolean;
  isLoadingMoreChannelVideos: boolean;
  isLoadingAudioFeatures: boolean;

  error: string | null;
  channelVideosError: string | null;
  audioFeaturesError: string | null;

  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  } | null;

  currentChannelId: string | null;
  hasMore: boolean;
}

const ANALYZING_STORAGE_KEY = 'meradio-analyzing';

function readPersistedAnalyzing(): { [videoId: string]: number } {
  try {
    const raw = sessionStorage.getItem(ANALYZING_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { [videoId: string]: number };
    const fresh: { [videoId: string]: number } = {};
    Object.entries(parsed).forEach(([videoId, startedAt]) => {
      if (Date.now() - startedAt < 10 * 60 * 1000) fresh[videoId] = startedAt;
    });
    return fresh;
  } catch {
    return {};
  }
}

function persistAnalyzing(analyzing: { [videoId: string]: number }): void {
  try {
    sessionStorage.setItem(ANALYZING_STORAGE_KEY, JSON.stringify(analyzing));
  } catch {
    return;
  }
}

const initialState: VideosState = {
  analyzing: readPersistedAnalyzing(),
  videos: [],
  channelVideos: {},
  selectedVideo: null,
  audioFeatures: {},
  likedVideos: [],
  isLoading: false,
  isLoadingChannelVideos: false,
  isLoadingMoreChannelVideos: false,
  isLoadingAudioFeatures: false,
  error: null,
  channelVideosError: null,
  audioFeaturesError: null,
  pagination: null,
  currentChannelId: null,
  hasMore: true,
};

export const getChannelVideos = createAsyncThunk(
  'videos/getChannelVideos',
  async ({
    channelId,
    limit = 20,
    offset = 0,
    sortBy = 'recent'
  }: {
    channelId: string;
    limit?: number;
    offset?: number;
    sortBy?: 'recent' | 'oldest' | 'title';
    reset?: boolean;
  }) => {
    const response = await channelsApi.getChannelVideos({ channelId, limit, offset, sortBy });
    return { channelId, response };
  }
);

export const getVideos = createAsyncThunk(
  'videos/getVideos',
  async ({
    limit = 20,
    offset = 0,
    channelId,
    sortBy = 'recent'
  }: {
    limit?: number;
    offset?: number;
    channelId?: string;
    sortBy?: 'recent' | 'oldest' | 'title';
  } = {}) => {
    const response = await videosApi.getVideos({ limit, offset, channelId, sortBy });
    return response;
  }
);

export const findSimilarVideos = createAsyncThunk(
  'videos/findSimilarVideos',
  async ({ videoId, threshold = 0.8, limit = 10 }: {
    videoId: string;
    threshold?: number;
    limit?: number;
  }) => {
    const response = await api.get<Video[]>(`/audio/similar/${videoId}`, {
      params: { threshold, limit }
    });
    if (!response.success || !response.data) {
      throw new Error('Failed to find similar videos');
    }
    return response.data;
  }
);

export const getVideosByBPM = createAsyncThunk(
  'videos/getVideosByBPM',
  async ({ minBpm, maxBpm, limit = 25 }: {
    minBpm: number;
    maxBpm: number;
    limit?: number;
  }) => {
    const response = await api.get<Video[]>('/audio/by-bpm', {
      params: { minBpm, maxBpm, limit }
    });
    if (!response.success || !response.data) {
      throw new Error('Failed to get videos by BPM');
    }
    return response.data;
  }
);

export const getVideosByKey = createAsyncThunk(
  'videos/getVideosByKey',
  async ({ key, mode, limit = 25 }: {
    key: number;
    mode?: number;
    limit?: number;
  }) => {
    const response = await api.get<Video[]>('/audio/by-key', {
      params: { key, mode, limit }
    });
    if (!response.success || !response.data) {
      throw new Error('Failed to get videos by key');
    }
    return response.data;
  }
);

const videosSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {
    startAnalyzing: (state, action: PayloadAction<{ videoId: string; startedAt: number }>) => {
      state.analyzing[action.payload.videoId] = action.payload.startedAt;
      persistAnalyzing(state.analyzing);
    },
    stopAnalyzing: (state, action: PayloadAction<string>) => {
      delete state.analyzing[action.payload];
      persistAnalyzing(state.analyzing);
    },
    setLikedVideos: (state, action: PayloadAction<string[]>) => {
      state.likedVideos = action.payload;
    },
    addLikedVideo: (state, action: PayloadAction<string>) => {
      if (!state.likedVideos.includes(action.payload)) {
        state.likedVideos.push(action.payload);
      }
    },
    removeLikedVideo: (state, action: PayloadAction<string>) => {
      state.likedVideos = state.likedVideos.filter(id => id !== action.payload);
    },
    selectVideo: (state, action: PayloadAction<Video | null>) => {
      state.selectedVideo = action.payload;
    },

    clearErrors: (state) => {
      state.error = null;
      state.channelVideosError = null;
      state.audioFeaturesError = null;
    },

    setCurrentChannel: (state, action: PayloadAction<string | null>) => {
      state.currentChannelId = action.payload;
    },

    clearChannelVideos: (state, action: PayloadAction<string>) => {
      const channelId = action.payload;
      delete state.channelVideos[channelId];
    },

    toggleVideoLike: (state, action: PayloadAction<{ videoId: string; liked: boolean }>) => {
      const { videoId, liked } = action.payload;
      if (liked) {
        if (!state.likedVideos.includes(videoId)) {
          state.likedVideos.push(videoId);
        }
      } else {
        state.likedVideos = state.likedVideos.filter(id => id !== videoId);
      }
    },

    resetVideosState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChannelVideos.pending, (state, action) => {
        const isReset = action.meta.arg.reset ?? (action.meta.arg.offset ?? 0) === 0;
        if (isReset) {
          state.isLoadingChannelVideos = true;
        } else {
          state.isLoadingMoreChannelVideos = true;
        }
        state.channelVideosError = null;
      })
      .addCase(getChannelVideos.fulfilled, (state, action) => {
        state.isLoadingChannelVideos = false;
        state.isLoadingMoreChannelVideos = false;
        const { channelId, response } = action.payload;
        const { data, pagination } = response;
        const isReset = action.meta.arg.reset ?? (action.meta.arg.offset ?? 0) === 0;

        const videos = data.map((item: any) => {
          const video = { ...item };
          delete video.isLiked;
          delete video.audioFeatures;
          return video;
        });

        if (isReset) {
          state.channelVideos[channelId] = videos;
        } else {
          const existing = state.channelVideos[channelId] ?? [];
          const seen = new Set(existing.map((v: Video) => v.videoId));
          state.channelVideos[channelId] = [
            ...existing,
            ...videos.filter((v: Video) => !seen.has(v.videoId)),
          ];
        }
        state.videos = state.channelVideos[channelId];
        state.pagination = pagination ? {
          limit: pagination.limit,
          offset: pagination.offset,
          total: pagination.total,
          hasMore: pagination.hasMore
        } : null;
        state.currentChannelId = channelId;
        state.hasMore = pagination?.hasMore || false;

        data.forEach((item: any) => {
          const videoId = item.id || item._id;
          if (item.isLiked && !state.likedVideos.includes(videoId)) {
            state.likedVideos.push(videoId);
          }
          if (item.audioFeatures) {
            state.audioFeatures[item.videoId || item.video_id || videoId] = item.audioFeatures;
          }
        });

        state.channelVideosError = null;
      })
      .addCase(getChannelVideos.rejected, (state, action) => {
        state.isLoadingChannelVideos = false;
        state.isLoadingMoreChannelVideos = false;
        state.channelVideosError = action.error.message || 'Failed to get channel videos';
      });

    builder
      .addCase(getVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, pagination } = action.payload;

        const videos = data.map((item: any) => {
          const video = { ...item };
          delete video.isLiked;
          delete video.audioFeatures;
          return video;
        });

        state.videos = videos;
        state.pagination = pagination ? {
          limit: pagination.limit,
          offset: pagination.offset,
          total: pagination.total,
          hasMore: pagination.hasMore
        } : null;
        state.hasMore = pagination?.hasMore || false;

        data.forEach((item: any) => {
          const videoId = item.id || item._id;
          if (item.isLiked && !state.likedVideos.includes(videoId)) {
            state.likedVideos.push(videoId);
          }
          if (item.audioFeatures) {
            state.audioFeatures[item.videoId || item.video_id || videoId] = item.audioFeatures;
          }
        });

        state.error = null;
      })
      .addCase(getVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get videos';
      });

    builder
      .addCase(findSimilarVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(findSimilarVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videos = action.payload;
        state.error = null;
      })
      .addCase(findSimilarVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to find similar videos';
      });

    builder
      .addCase(getVideosByBPM.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVideosByBPM.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videos = action.payload;
        state.error = null;
      })
      .addCase(getVideosByBPM.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get videos by BPM';
      });

    builder
      .addCase(getVideosByKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVideosByKey.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videos = action.payload;
        state.error = null;
      })
      .addCase(getVideosByKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get videos by key';
      });
  },
});

export const {
  startAnalyzing,
  stopAnalyzing,
  selectVideo,
  clearErrors,
  setCurrentChannel,
  clearChannelVideos,
  resetVideosState,
  toggleVideoLike,
  setLikedVideos,
  addLikedVideo,
  removeLikedVideo,
} = videosSlice.actions;

export const selectVideosState = (state: { videos: VideosState }) => state.videos;
export const selectVideos = (state: { videos: VideosState }) => state.videos.videos;
export const selectChannelVideos = (channelId: string) =>
  (state: { videos: VideosState }) => state.videos.channelVideos[channelId] || [];
export const selectSelectedVideo = (state: { videos: VideosState }) => state.videos.selectedVideo;
export const selectVideoAudioFeatures = (videoId: string) =>
  (state: { videos: VideosState }) => state.videos.audioFeatures[videoId];
export const selectAllAudioFeatures = (state: { videos: VideosState }) => state.videos.audioFeatures;
export const selectIsVideosLoading = (state: { videos: VideosState }) => state.videos.isLoading;
export const selectIsLoadingChannelVideos = (state: { videos: VideosState }) => state.videos.isLoadingChannelVideos;
export const selectIsLoadingMoreChannelVideos = (state: { videos: VideosState }) => state.videos.isLoadingMoreChannelVideos;
export const selectChannelVideosHasMore = (state: { videos: VideosState }) => state.videos.hasMore;
export const selectVideosError = (state: { videos: VideosState }) => state.videos.error;
export const selectChannelVideosError = (state: { videos: VideosState }) => state.videos.channelVideosError;
export const selectVideosPagination = (state: { videos: VideosState }) => state.videos.pagination;

export const selectIsVideoLiked = (videoId: string) =>
  (state: { videos: VideosState }) => state.videos.likedVideos.includes(videoId);
export const selectLikedVideos = (state: { videos: VideosState }) => state.videos.likedVideos;

export default videosSlice.reducer;
